import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass/styled-components';
import { ProfilePicture, StatusIndicator, Button, AsyncButton } from '@getro/rombo';
import { X } from 'lucide-react';
import { getUserDisplayName } from '../../../helpers/users';

export const AdminListItem = ({
  onRemoveAdmin,
  onResendInvitation,
  isResendingInvitation,
  user,
  showEmailAddress,
  hasAcceptedInvitation,
  ...rebassProps
}) => {
  const displayName = useMemo(() => getUserDisplayName(user), [user]);
  const hasAccepted = useMemo(
    () =>
      hasAcceptedInvitation !== null && hasAcceptedInvitation !== undefined
        ? hasAcceptedInvitation
        : user.onboardingCompleted,
    [user.onboardingCompleted, hasAcceptedInvitation],
  );
  const userStatus = useMemo(() => (hasAccepted ? 'Invitation accepted' : 'Invitation pending - '), [hasAccepted]);
  const statusColor = useMemo(() => (hasAccepted ? 'success' : 'warning'), [hasAccepted]);

  return (
    <Flex alignItems="center" p="8px" data-testid="admin-list-item" {...rebassProps}>
      <Flex width={1 / 2} sx={{ gap: '8px' }}>
        <Box width="40px" minWidth="40px">
          <ProfilePicture imageUrl={user.avatarUrl} name={displayName} />
        </Box>
        <Flex width={1 / 2} flexDirection="column">
          <Text data-testid="display-name" fontWeight="500" color="text.dark" fontSize="14px">
            {displayName}
          </Text>
          {showEmailAddress && (
            <Text
              data-testid="email"
              color="text.subtle"
              fontSize="14px"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user.email}
            </Text>
          )}
        </Flex>
      </Flex>
      <Flex width={1 / 2} justifyContent="space-between" alignItems="center">
        <Flex sx={{ alignItems: 'center', '*': { color: 'text.main', fontSize: '14px' } }}>
          <StatusIndicator status={userStatus} statusColor={statusColor} />
          {!hasAccepted && (
            <>
              {!user.invitationSent && onResendInvitation && (
                <AsyncButton
                  data-testid="resend-invite"
                  width="max-content"
                  color="text.subtle"
                  variant="underlineLink"
                  pl={2}
                  m={0}
                  loading={isResendingInvitation}
                  onClick={() => {
                    onResendInvitation(user.id);
                  }}
                >
                  Resend invite
                </AsyncButton>
              )}
              {user.invitationSent && (
                <Text color="text.subtle" data-testid="sent-invite">
                  Invitation email sent
                </Text>
              )}
            </>
          )}
        </Flex>
        {onRemoveAdmin && (
          <Button
            data-testid="remove-admin"
            variant="tertiary"
            color="neutral.600"
            size="small"
            p={2}
            title="Remove"
            onClick={() => {
              onRemoveAdmin(user);
            }}
          >
            <Box
              as={X}
              height="16px"
              width="16px"
              sx={{ minWidth: '16px', minHeight: '16px' }}
              strokeWidth="1.5"
              aria-hidden="true"
            />
            <Text variant="srOnly">Remove</Text>
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

AdminListItem.propTypes = {
  user: PropTypes.object.isRequired,
  showEmailAddress: PropTypes.bool.isRequired,
  onResendInvitation: PropTypes.func,
  onRemoveAdmin: PropTypes.func,
  isResendingInvitation: PropTypes.bool,
  hasAcceptedInvitation: PropTypes.bool,
};

AdminListItem.defaultProps = {
  onResendInvitation: null,
  onRemoveAdmin: null,
  isResendingInvitation: false,
  hasAcceptedInvitation: null,
};
